import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { styles } from './styles';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import lodash from 'lodash';
import { getErrorKey } from '~/helpers/utils';
import { errorMessages } from '~/constants/errorMessages';

interface IYesNoQuestionProps {
  question?: string;
  name: string;
  tooltipTitle?: React.ReactNode;
  onChange?: (value: boolean) => void;
  onValueChange?: (params: {
    event: React.ChangeEvent<HTMLInputElement>;
    fieldOnChange: (value: string) => void;
  }) => void;
  containerStyles?: (theme: Theme) => SxProps<Theme>;
  disableFieldOnChange?: boolean;
  labelYes?: string;
  valueYes?: string;
  labelNo?: string;
  valueNo?: string;
  disabled?: boolean;
  showDefaultError?: boolean;
}

export const FormYesNoQuestion = ({
  question,
  tooltipTitle,
  name,
  onChange,
  onValueChange,
  containerStyles,
  labelYes = 'Так',
  valueYes = 'true',
  labelNo = 'Ні',
  valueNo = 'false',
  disableFieldOnChange,
  disabled,
  showDefaultError,
}: IYesNoQuestionProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = lodash.get(errors, name) as FieldError;
  const errorDataTestId = [`${name}`, getErrorKey(error?.message || '')].filter(Boolean).join('-');
  const errorMessage = showDefaultError
    ? errorMessages.ANSWER_REQUIRED
    : (error as { message?: string })?.message;

  return (
    <Box sx={(theme) => ({ ...styles.container, ...containerStyles?.(theme) })} data-name={name}>
      <FormControl sx={{ p: 0 }} error={!!error}>
        {question && (
          <Typography
            variant="body2"
            sx={{ alignItems: 'center', display: 'flex', gap: 1, lineHeight: 2 }}
          >
            {question}
            {tooltipTitle && (
              <Tooltip title={tooltipTitle} placement="right">
                <InfoOutlineIcon sx={styles.infoIcon} />
              </Tooltip>
            )}
          </Typography>
        )}
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <>
              <RadioGroup
                value={field.value?.toString() ?? ''}
                onChange={(e) => {
                  if (onValueChange) {
                    onValueChange({
                      event: e,
                      fieldOnChange: field.onChange,
                    });
                    return;
                  }
                  const isChecked = e.target.value === 'true';
                  !disableFieldOnChange && field.onChange(isChecked);
                  onChange?.(isChecked);
                }}
                row
                sx={{ gap: 2 }}
              >
                <FormControlLabel
                  value={valueYes}
                  disabled={disabled}
                  control={
                    <Radio
                      slotProps={{
                        input: {
                          'data-testid': `${name}-${valueYes}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>,
                      }}
                    />
                  }
                  label={labelYes}
                  sx={styles.radioBtn}
                />
                <FormControlLabel
                  value={valueNo}
                  disabled={disabled}
                  control={
                    <Radio
                      slotProps={{
                        input: {
                          'data-testid': `${name}-${valueNo}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>,
                      }}
                    />
                  }
                  label={labelNo}
                  sx={styles.radioBtn}
                />
              </RadioGroup>
              <FormHelperText sx={{ top: '100%' }} data-testid={errorDataTestId}>
                {errorMessage}
              </FormHelperText>
            </>
          )}
        />
      </FormControl>
    </Box>
  );
};
